/* import __COLOCATED_TEMPLATE__ from './manage-devices-modal.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import { request, ResponseError } from 'embercom/lib/inbox/requests';
import type PermissionsModal from 'embercom/services/permissions-modal';

interface DeviceTokenWire {
  type: string;
  id: string;
  platform: string;
  device_id: string | null;
  created_at: number;
  updated_at: number;
}

interface DeviceToken {
  id: string;
  platform: string;
  deviceId: string;
  createdAt: string;
}

interface Args {
  isOpen: boolean;
  userName: string;
  userId?: string;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

const PLATFORM_SLUG_MAP: Record<string, string> = {
  'iOS Production': 'ios',
  'iOS Development': 'ios-dev',
  Android: 'android',
  Chrome: 'chrome',
  Firefox: 'firefox',
};

function platformSlug(apiPlatform: string): string {
  return PLATFORM_SLUG_MAP[apiPlatform] ?? 'unknown';
}

function deserializeDeviceToken(wire: DeviceTokenWire): DeviceToken {
  return {
    id: wire.id,
    platform: platformSlug(wire.platform),
    deviceId: wire.device_id ?? wire.id,
    createdAt: new Date(wire.created_at * 1000).toLocaleDateString(),
  };
}

export default class ManageDevicesModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare permissionsModal: PermissionsModal;
  @service declare session: Session;

  @tracked devices: DeviceToken[] = [];
  @tracked isLoading = false;
  @tracked isLoadError = false;

  get title(): string {
    return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.modal-title', {
      userName: this.args.userName,
    });
  }

  get hasDevices(): boolean {
    return this.devices.length > 0;
  }

  @action platformLabel(platform: string): string {
    switch (platform) {
      case 'ios':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-ios');
      case 'ios-dev':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-ios-dev');
      case 'android':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-android');
      case 'chrome':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-chrome');
      case 'firefox':
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-firefox');
      default:
        return this.intl.t('inbox.attribute-list-item.user-data-menu.devices.platform-unknown');
    }
  }

  @action async loadDevices() {
    this.devices = [];
    this.isLoading = true;
    this.isLoadError = false;
    try {
      let response = await request(
        `/ember/users/${this.args.userId}/device_tokens?app_id=${this.session.workspace.id}`,
      );
      let json: { type: string; data: DeviceTokenWire[] } = await response.json();
      this.devices = json.data.map(deserializeDeviceToken);
    } catch (e: unknown) {
      if (e instanceof ResponseError && e.response.status === 403) {
        try {
          let json = (await e.response.clone().json()) as { required_permission?: string };
          if (json.required_permission) {
            this.permissionsModal.showModal({ permissionsLabel: json.required_permission });
            return;
          }
        } catch {
          // JSON parsing failed, fall through to generic error
        }
      }
      this.isLoadError = true;
      this.devices = [];
    } finally {
      this.isLoading = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::ManageDevicesModal': typeof ManageDevicesModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/manage-devices-modal': typeof ManageDevicesModal;
  }
}
